// gate-registry-waive-invalidate.ts
//
// waiveGate/invalidateGateOnShaChange split out of gate-registry-transitions.ts
// to keep both files ≤200 lines — see gate-registry.ts's header comment.
//
// Rules enforced here (F07):
//  - G2 waive requires authority_ref; the actor is recorded as "waived", never
//    fabricated as "approved by a person" (spec: "waived chỉ ghi quyền ủy
//    nhiệm, không ghi thành user-approved").
//  - invalidate() clears an approved/waived/pending gate when the candidate
//    artifact hash changes — approval does not survive a sha change.

import { carryAuthorityGrant } from "./gate-authority-grant-carryover.js";
import {
  type GateId,
  type GateReceipt,
  type GateRecord,
  readGateFile,
  writeGateFile,
} from "./gate-registry.js";

export interface GateWaiveInput {
  gate: GateId;
  requestId: string;
  authorityRef: string;
  reason?: string;
  now?: () => Date;
  targets?: string[];
  expiresAt?: string;
}

/**
 * G2 waive: records delegated authority (authority_ref), never a fabricated
 * user-approved actor. Only meaningful for G2 per spec, but the guard is
 * caller-facing documentation here — this function does not hardcode gate id
 * "g2" so tests can exercise the rule directly.
 */
export async function waiveGate(controlRoot: string, input: GateWaiveInput): Promise<GateReceipt> {
  const gates = await readGateFile(controlRoot);
  const record = gates[input.gate];

  if (record.status === "waived" && record.request_id === input.requestId) {
    return {
      gate: input.gate,
      status: "waived",
      request_id: record.request_id,
      at: record.at,
      reused: true,
    };
  }

  const now = input.now ?? (() => new Date());
  const next: GateRecord = {
    status: "waived",
    request_id: input.requestId,
    authority_ref: input.authorityRef,
    reason: input.reason,
    at: now().toISOString(),
    ...carryAuthorityGrant(record, input),
    // actor_id intentionally OMITTED — waived is delegated authority, not a
    // person's approval act (spec §8: "không ghi thành user-approved").
  };
  gates[input.gate] = next;
  await writeGateFile(controlRoot, gates, gates.revision);

  return {
    gate: input.gate,
    status: "waived",
    request_id: next.request_id,
    at: next.at,
    reused: false,
  };
}

/**
 * Invalidate a gate when the candidate/artifact sha changes — approval does
 * not survive a sha change (spec §4/§8). No-op (returns unchanged) if the
 * gate isn't approved/waived/pending, or if the hash hasn't actually changed.
 */
export async function invalidateGateOnShaChange(
  controlRoot: string,
  gate: GateId,
  newArtifactHash: string,
  now: () => Date = () => new Date(),
): Promise<GateRecord> {
  const gates = await readGateFile(controlRoot);
  const record = gates[gate];

  if (record.artifact_hash === newArtifactHash) return record;
  if (record.status === "not_requested" || record.status === "invalidated") return record;

  const next: GateRecord = {
    status: "invalidated",
    request_id: record.request_id,
    artifact_hash: record.artifact_hash,
    reason: `artifact_hash đổi (${record.artifact_hash ?? "(none)"} → ${newArtifactHash}) — approval không sống sót qua thay đổi candidate.`,
    at: now().toISOString(),
  };
  gates[gate] = next;
  await writeGateFile(controlRoot, gates, gates.revision);
  return next;
}
